import React, { useState, useEffect, useCallback } from 'react';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import axios from 'axios';

const ImportLeaves = ({
    show,
    onHide,
    onImport,
    canImportLeaves,
    error,
    setError,
    API_BASE
}) => {
    const [csvData, setCsvData] = useState([]);
    const [, setImportProgress] = useState(0);
    const [isImporting, setIsImporting] = useState(false);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(false);
    const [selectedLeaves, setSelectedLeaves] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [columnNamingOption, setColumnNamingOption] = useState('name'); // 'name' or 'code'

    // Fetch leave types from leave_master
    const fetchLeaveTypes = useCallback(async () => {
        try {
            setLoadingLeaveTypes(true);
            const token = localStorage.getItem('authToken');

            const response = await axios.get(
                `${API_BASE}imports/get_leave_types.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setLeaveTypes(response.data.leaveTypes || []);
            }
        } catch (error) {
            console.error('Error fetching leave types:', error);
            setError('Failed to load leave types');
        } finally {
            setLoadingLeaveTypes(false);
        }
    }, [API_BASE, setError]);

    // Fetch employees for validation
    const fetchEmployees = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(
                `${API_BASE}/get_employees.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setEmployees(response.data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }, [API_BASE]);

    useEffect(() => {
        if (show) {
            fetchLeaveTypes();
            fetchEmployees();
        }
    }, [show, fetchLeaveTypes, fetchEmployees]);

    const downloadTemplate = () => {
        if (selectedLeaves.length === 0) {
            alert('Please select at least one leave type first');
            return;
        }

        // Get employee codes
        const employeeCodes = employees
            .filter(emp => emp.status === 'Active')
            .map(emp => emp.emp_code);

        // Determine column header based on naming option
        const getColumnHeader = (leave) => {
            return columnNamingOption === 'code' ? leave.leave_code : leave.leave_name;
        };

        // Create template data structure (crosstab format)
        const headerRow = ['emp_code', 'date', ...selectedLeaves.map(leave => getColumnHeader(leave))];

        // Create sample data with a few employees
        const sampleData = employeeCodes.slice(0, 5).map(empCode => {
            const row = { 
                emp_code: empCode,
                date: new Date().toISOString().split('T')[0]
            };
            selectedLeaves.forEach(leave => {
                row[getColumnHeader(leave)] = leave.leave_type_id === 2 ? '1.5' : '1.0';
            });
            return row;
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(sampleData, { header: headerRow });

        // Set column widths
        const wscols = [
            { wch: 15 }, // emp_code
            { wch: 12 }, // date
            ...selectedLeaves.map(() => ({ wch: 20 }))
        ];
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Leave Import");

        // Add instructions sheet
        const instructions = [
            ['LEAVE IMPORT TEMPLATE - INSTRUCTIONS'],
            [''],
            ['COLUMN NAMING OPTION USED: ' + (columnNamingOption === 'code' ? 'LEAVE CODE' : 'LEAVE NAME')],
            [''],
            ['HOW TO FILL THIS TEMPLATE:'],
            ['1. Fill values in the appropriate columns for each employee'],
            ['2. Date format: YYYY-MM-DD (e.g., 2026-02-13)'],
            ['3. Use decimal values for half days (e.g., 0.5, 1.5, 2.75)'],
            ['4. Leave blank if no leave for that employee on that date'],
            [''],
            ['COLUMN DESCRIPTIONS:'],
            ['emp_code - Employee code (must match existing employee codes)'],
            ['date - Date of leave (YYYY-MM-DD)'],
            ...selectedLeaves.map(leave => [
                `${getColumnHeader(leave)} - ${leave.leave_name} (${leave.leave_code}) - ${leave.frequency || 'Yearly'} leave`
            ]),
            [''],
            ['IMPORTANT NOTES:'],
            ['1. Do not change column names'],
            ['2. Ensure column names exactly match: ' + selectedLeaves.map(l => getColumnHeader(l)).join(', ')],
            ['3. Employee codes must be correct and active'],
            ['4. Dates must be valid and in YYYY-MM-DD format'],
            ['5. Save file as CSV or Excel before importing']
        ];

        const ws2 = XLSX.utils.aoa_to_sheet(instructions);
        ws2['!cols'] = [{ wch: 80 }];
        XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

        // Add validation sheet
        const validation = [
            ['DATA VALIDATION'],
            [''],
            ['Field', 'Type', 'Required', 'Format', 'Example'],
            ['emp_code', 'Text', 'Yes', 'Existing employee code', '10004'],
            ['date', 'Date', 'Yes', 'YYYY-MM-DD', '2026-02-13'],
            ...selectedLeaves.map(leave => [
                getColumnHeader(leave),
                'Decimal',
                'No',
                'Number (e.g., 1.0, 0.5, 2.5)',
                '1.5'
            ])
        ];

        const ws3 = XLSX.utils.aoa_to_sheet(validation);
        ws3['!cols'] = [
            { wch: 25 },
            { wch: 15 },
            { wch: 10 },
            { wch: 30 },
            { wch: 15 }
        ];
        XLSX.utils.book_append_sheet(wb, ws3, "Validation Rules");

        // Add available leave types sheet
        const leaveTypesList = [
            ['AVAILABLE LEAVE TYPES IN SYSTEM'],
            [''],
            ['Leave Name', 'Leave Code', 'Frequency', 'Status'],
            ...selectedLeaves.map(leave => [
                leave.leave_name,
                leave.leave_code,
                leave.frequency || 'Yearly',
                leave.status === 1 ? 'Active' : 'Inactive'
            ])
        ];

        const ws4 = XLSX.utils.aoa_to_sheet(leaveTypesList);
        ws4['!cols'] = [
            { wch: 25 },
            { wch: 15 },
            { wch: 15 },
            { wch: 15 }
        ];
        XLSX.utils.book_append_sheet(wb, ws4, "Available Leave Types");

        // Save file
        const fileName = `leave_import_template_${columnNamingOption}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const handleFileUpload = (event) => {
        if (!canImportLeaves) {
            alert('You do not have permission to import leaves');
            return;
        }

        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name.toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

        if (isExcel) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { raw: false, defval: '' });

                    if (jsonData && jsonData.length > 0) {
                        setCsvData(jsonData);
                        setError('');
                    } else {
                        setError('No valid data found in Excel file');
                    }
                } catch (error) {
                    setError('Error reading Excel file: ' + error.message);
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    if (results.data && results.data.length > 0) {
                        setCsvData(results.data);
                        setError('');
                    } else {
                        setError('No valid data found in CSV file');
                    }
                },
                error: (error) => {
                    setError('Error parsing CSV file: ' + error.message);
                }
            });
        }
    };

    const handleImport = async () => {
        if (!canImportLeaves) {
            alert('You do not have permission to import leaves');
            return;
        }

        if (!csvData.length) {
            setError('No data to import');
            return;
        }

        setIsImporting(true);
        setImportProgress(0);
        setError('');

        try {
            await onImport(csvData, setImportProgress);
            onHide();
            setCsvData([]);
            setSelectedLeaves([]);
        } catch (error) {
            setError('Import failed: ' + error.message);
        } finally {
            setIsImporting(false);
        }
    };

    const handleClose = () => {
        setCsvData([]);
        setImportProgress(0);
        setIsImporting(false);
        setSelectedLeaves([]);
        onHide();
    };

    const toggleLeave = (leave) => {
        setSelectedLeaves(prev => {
            const exists = prev.find(l => l.leave_type_id === leave.leave_type_id);
            if (exists) {
                return prev.filter(l => l.leave_type_id !== leave.leave_type_id);
            } else {
                return [...prev, leave];
            }
        });
    };

    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">
                            <i className="bi bi-upload me-2"></i>
                            Import Leaves
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleClose}
                            disabled={isImporting}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <div className="row g-4">
                            {/* Leave Type Selection */}
                            <div className="col-12">
                                <div className="card border-primary">
                                    <div className="card-header bg-primary text-white">
                                        <h6 className="mb-0">
                                            <i className="bi bi-list-check me-2"></i>
                                            Step 1: Select Leave Types
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        {loadingLeaveTypes ? (
                                            <div className="text-center py-3">
                                                <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                                Loading leave types...
                                            </div>
                                        ) : leaveTypes.length === 0 ? (
                                            <div className="alert alert-warning">
                                                <i className="bi bi-exclamation-triangle me-2"></i>
                                                No leave types found in leave master. Please configure leave types first.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mb-3">
                                                    <small className="text-muted">
                                                        Select the leave types you want to include in the import template.
                                                        Selected: {selectedLeaves.length} of {leaveTypes.length}
                                                    </small>
                                                </div>
                                                <div className="row g-2">
                                                    {leaveTypes.map(leave => (
                                                        <div key={leave.leave_type_id} className="col-md-4">
                                                            <div className="form-check card border">
                                                                <div className="card-body py-2">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`leave-${leave.leave_type_id}`}
                                                                        checked={selectedLeaves.some(l => l.leave_type_id === leave.leave_type_id)}
                                                                        onChange={() => toggleLeave(leave)}
                                                                    />
                                                                    <label className="form-check-label ms-2" htmlFor={`leave-${leave.leave_type_id}`}>
                                                                        <strong>{leave.leave_name}</strong>
                                                                        <br />
                                                                        <small className="text-muted">
                                                                            Code: <span className="badge bg-secondary">{leave.leave_code}</span>
                                                                            {' '} | {' '}
                                                                            Freq: <span className="badge bg-info">{leave.frequency || 'Yearly'}</span>
                                                                        </small>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-3 text-center">
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => setSelectedLeaves(leaveTypes)}
                                                        disabled={selectedLeaves.length === leaveTypes.length}
                                                    >
                                                        <i className="bi bi-check-all me-2"></i>
                                                        Select All
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary ms-2"
                                                        onClick={() => setSelectedLeaves([])}
                                                        disabled={selectedLeaves.length === 0}
                                                    >
                                                        <i className="bi bi-x-circle me-2"></i>
                                                        Clear All
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Column Naming Option */}
                            {selectedLeaves.length > 0 && (
                                <div className="col-12">
                                    <div className="card border-warning">
                                        <div className="card-header bg-warning text-dark">
                                            <h6 className="mb-0">
                                                <i className="bi bi-tag me-2"></i>
                                                Step 2: Select Column Naming Convention
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-md-8">
                                                    <div className="btn-group" role="group">
                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="columnNaming"
                                                            id="optionName"
                                                            autoComplete="off"
                                                            checked={columnNamingOption === 'name'}
                                                            onChange={() => setColumnNamingOption('name')}
                                                        />
                                                        <label className="btn btn-outline-primary" htmlFor="optionName">
                                                            <i className="bi bi-fonts me-2"></i>
                                                            Use Leave Names
                                                            <small className="d-block text-muted">e.g., Casual Leave, Earned Leave</small>
                                                        </label>

                                                        <input
                                                            type="radio"
                                                            className="btn-check"
                                                            name="columnNaming"
                                                            id="optionCode"
                                                            autoComplete="off"
                                                            checked={columnNamingOption === 'code'}
                                                            onChange={() => setColumnNamingOption('code')}
                                                        />
                                                        <label className="btn btn-outline-success" htmlFor="optionCode">
                                                            <i className="bi bi-upc-scan me-2"></i>
                                                            Use Leave Codes
                                                            <small className="d-block text-muted">e.g., CL, EL, SL</small>
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 text-end">
                                                    <div className="alert alert-info py-2 mb-0">
                                                        <i className="bi bi-info-circle me-2"></i>
                                                        Column headers will be: {selectedLeaves.map(l => 
                                                            columnNamingOption === 'code' ? l.leave_code : l.leave_name
                                                        ).join(', ')}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Template Download */}
                            {selectedLeaves.length > 0 && (
                                <div className="col-12">
                                    <div className="card border-success">
                                        <div className="card-header bg-success text-white">
                                            <h6 className="mb-0">
                                                <i className="bi bi-download me-2"></i>
                                                Step 3: Download Template
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-md-8">
                                                    <h6 className="text-success">
                                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                                        Leave Import Template
                                                    </h6>
                                                    <p className="mb-2">
                                                        Template will include columns for: <strong>emp_code</strong>, <strong>date</strong> and{' '}
                                                        {selectedLeaves.map(leave => (
                                                            <span key={leave.leave_type_id} className="badge bg-light text-dark me-1 border">
                                                                {columnNamingOption === 'code' ? leave.leave_code : leave.leave_name}
                                                            </span>
                                                        ))}
                                                    </p>
                                                    <p className="mb-0 text-muted small">
                                                        <i className="bi bi-info-circle me-1"></i>
                                                        Column headers must exactly match the names shown above
                                                    </p>
                                                </div>
                                                <div className="col-md-4 text-end">
                                                    <button
                                                        className="btn btn-success btn-lg"
                                                        onClick={downloadTemplate}
                                                        disabled={selectedLeaves.length === 0}
                                                    >
                                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                                        Download Template
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* File Upload */}
                            {selectedLeaves.length > 0 && (
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0">
                                                <i className="bi bi-upload me-2"></i>
                                                Step 4: Upload Filled Template
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <label className="form-label">Select File *</label>
                                            <div className="input-group">
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept=".csv,.xlsx,.xls"
                                                    onChange={handleFileUpload}
                                                    disabled={isImporting}
                                                />
                                                <button
                                                    className="btn btn-outline-success"
                                                    type="button"
                                                    onClick={downloadTemplate}
                                                    title="Download Template"
                                                    disabled={isImporting}
                                                >
                                                    <i className="bi bi-download me-1"></i>
                                                    Template
                                                </button>
                                            </div>
                                            <div className="form-text">
                                                <i className="bi bi-exclamation-triangle text-warning me-1"></i>
                                                Column headers must exactly match: <strong>
                                                    emp_code, date, {selectedLeaves.map(l => 
                                                        columnNamingOption === 'code' ? l.leave_code : l.leave_name
                                                    ).join(', ')}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Data Preview */}
                            {csvData.length > 0 && (
                                <div className="col-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="card-title mb-0">
                                                    <i className="bi bi-table me-2"></i>
                                                    Preview ({csvData.length} records found)
                                                </h6>
                                                <span className="badge bg-primary">
                                                    {Object.keys(csvData[0]).filter(k => k !== 'emp_code' && k !== 'date').length} Leave Columns
                                                </span>
                                            </div>
                                            <div className="table-responsive" style={{ maxHeight: '250px' }}>
                                                <table className="table table-sm table-bordered">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            {Object.keys(csvData[0]).map(key => (
                                                                <th key={key} className="small">
                                                                    {key === 'emp_code' || key === 'date' ? (
                                                                        <span className="text-danger">{key}*</span>
                                                                    ) : (
                                                                        <span className="text-primary">{key}</span>
                                                                    )}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {csvData.slice(0, 5).map((row, index) => (
                                                            <tr key={index}>
                                                                {Object.entries(row).map(([key, value], i) => (
                                                                    <td
                                                                        key={i}
                                                                        className="small"
                                                                        style={{
                                                                            color: (key === 'emp_code' || key === 'date') && !value ? 'red' : 'inherit',
                                                                            backgroundColor: key !== 'emp_code' && key !== 'date' && value ? '#f0f9ff' : 'inherit'
                                                                        }}
                                                                    >
                                                                        {(key === 'emp_code' || key === 'date') && !value ? (
                                                                            <span className="text-danger">Required!</span>
                                                                        ) : (
                                                                            value || '-'
                                                                        )}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {csvData.length > 5 && (
                                                <div className="text-center small text-muted mt-2">
                                                    <i className="bi bi-arrow-down"></i>
                                                    ... and {csvData.length - 5} more records
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={isImporting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleImport}
                            disabled={!csvData.length || isImporting || selectedLeaves.length === 0}
                        >
                            {isImporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-upload me-2"></i>
                                    Start Import ({csvData.length} records)
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportLeaves;
